import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandShell from "../components/BrandShell";
import SEO from "../components/SEO";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Users, Search, Download, Ban, Trash2, RotateCcw, Sparkles, ShieldCheck,
  RefreshCcw, BadgeCheck,
} from "lucide-react";

const roleTint = {
  creator: "text-nexus-pink border-nexus-pink/30 bg-nexus-pink/10",
  brand: "text-nexus-blue border-nexus-blue/30 bg-nexus-blue/10",
};

function StatPill({ icon: Icon, label, value, tone }) {
  return (
    <div className="glass rounded-2xl p-4 min-w-[130px]" data-testid={`signups-stat-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${tone}`} /> {label}
      </div>
      <div className="font-display font-black text-2xl tabular-nums text-white">
        {Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso); }
}

export default function AdminSignups() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { role, status, limit: 500 };
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/admin/signups", { params });
      setRows(r.data?.rows || []);
      setTotals(r.data?.totals || {});
    } catch (e) {
      const code = e.response?.status;
      if (code === 403) { toast.error("Admin only"); nav("/"); return; }
      if (code === 401) { toast.error("Log in first"); nav("/auth?mode=login"); return; }
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [role, status]);

  const onSearchSubmit = (e) => { e.preventDefault(); load(); };

  const suspend = async (u) => {
    if (!window.confirm(`Suspend ${u.name || u.email}? They won't be able to log in.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/suspend`);
      setRows((rs) => rs.map((r) => (r.id === u.id ? { ...r, is_suspended: true } : r)));
      toast.success("Suspended");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unsuspend = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/unsuspend`);
      setRows((rs) => rs.map((r) => (r.id === u.id ? { ...r, is_suspended: false } : r)));
      toast.success("Reinstated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name || u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setRows((rs) => rs.filter((r) => r.id !== u.id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const header = ["name","email","role","company","industry","location","followers","subscription","auth","suspended","joined"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [header.join(",")].concat(
      rows.map((r) => [
        r.name, r.email, r.role, r.company, r.industry, r.location,
        r.follower_count, r.subscription_tier, r.auth_provider,
        r.is_suspended ? "yes" : "no", r.created_at,
      ].map(escape).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nexus-signups-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const shown = useMemo(() => rows.length, [rows]);

  return (
    <BrandShell title="Admin · Signups">
      <SEO title="Admin · Signups · Nexus" description="Every user on the platform." path="/admin/signups" />
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-nexus-pink" /> Admin only
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tighter">Signups tracker</h1>
            <p className="text-zinc-400 mt-1.5 text-sm">Every account on the platform. Search, filter, moderate.</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={load}
              variant="outline"
              className="rounded-full border-white/10 text-white hover:bg-white/5 h-10"
              data-testid="signups-refresh-btn"
            >
              <RefreshCcw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={exportCsv}
              disabled={rows.length === 0}
              className="nexus-gradient text-white border-0 font-semibold rounded-full h-10 disabled:opacity-50"
              data-testid="signups-export-btn"
            >
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-3 flex-wrap" data-testid="signups-stats">
          <StatPill icon={Users} label="Total" value={totals.total} tone="text-white" />
          <StatPill icon={Sparkles} label="Creators" value={totals.creators} tone="text-nexus-pink" />
          <StatPill icon={BadgeCheck} label="Brands" value={totals.brands} tone="text-nexus-blue" />
          <StatPill icon={ShieldCheck} label="Today" value={totals.today} tone="text-emerald-300" />
          <StatPill icon={Ban} label="Suspended" value={totals.suspended} tone="text-red-300" />
        </div>

        {/* Filters */}
        <div className="glass rounded-2xl p-4 flex items-center gap-3 flex-wrap" data-testid="signups-filters">
          <form onSubmit={onSearchSubmit} className="flex-1 min-w-[240px]">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email or company…"
                className="pl-9 bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600"
                data-testid="signups-search-input"
              />
            </div>
          </form>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-[160px] bg-white/[0.02] border-white/10 text-white" data-testid="signups-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0C0C10] border-white/10 text-white">
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="creator">Creators</SelectItem>
              <SelectItem value="brand">Brands</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[160px] bg-white/[0.02] border-white/10 text-white" data-testid="signups-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0C0C10] border-white/10 text-white">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="glass rounded-2xl overflow-hidden" data-testid="signups-table">
          <div className="grid grid-cols-[minmax(180px,1.4fr)_minmax(180px,1.6fr)_100px_140px_140px_180px] gap-4 px-4 py-3 border-b border-white/5 text-[10px] uppercase tracking-widest text-zinc-500 sticky top-0 bg-[#0A0A0E]/80 backdrop-blur">
            <span>Name</span>
            <span>Email</span>
            <span>Role</span>
            <span>Joined</span>
            <span>Auth · Meta</span>
            <span className="text-right pr-2">Actions</span>
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-white/5">
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="px-4 py-3 animate-pulse">
                  <div className="h-6 w-1/2 bg-white/5 rounded" />
                </div>
              ))
            ) : shown === 0 ? (
              <div className="p-12 text-center text-zinc-400" data-testid="signups-empty">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Nothing to show</div>
                <div className="font-display text-2xl mb-2">No signups match those filters</div>
                <p className="text-zinc-500">Try clearing the search or switching filters.</p>
              </div>
            ) : (
              rows.map((r) => (
                <div
                  key={r.id}
                  className={`grid grid-cols-[minmax(180px,1.4fr)_minmax(180px,1.6fr)_100px_140px_140px_180px] gap-4 px-4 py-3 items-center text-sm ${r.is_suspended ? "bg-red-500/5" : "hover:bg-white/[0.02]"}`}
                  data-testid={`signup-row-${r.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {r.avatar_url ? (
                      <img src={r.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full nexus-gradient flex items-center justify-center text-white text-xs font-bold shrink-0">
                        {(r.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold truncate flex items-center gap-1.5">
                        {r.name || "—"}
                        {r.is_suspended && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-red-500/20 text-red-200 border border-red-500/30">Suspended</span>}
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {r.company || r.location || (r.niches?.slice(0,2).join(" · "))}
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0 truncate text-zinc-300">{r.email}</div>
                  <div>
                    <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${roleTint[r.role] || "bg-white/5 text-zinc-400 border-white/10"}`}>
                      {r.role}
                    </span>
                  </div>
                  <div className="text-[11px] text-zinc-400">{fmtDate(r.created_at)}</div>
                  <div className="text-[11px] text-zinc-500 flex flex-col leading-tight">
                    <span>{r.auth_provider || "email"}</span>
                    {r.role === "creator" && typeof r.verified_count === "number" && (
                      <span className="text-emerald-300/80">{r.verified_count}/7 verified</span>
                    )}
                    {r.role === "brand" && r.subscription_tier && (
                      <span className="text-nexus-blue/80">{r.subscription_tier}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-1.5">
                    {r.is_suspended ? (
                      <button
                        onClick={() => unsuspend(r)}
                        data-testid={`signup-unsuspend-${r.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 hover:bg-emerald-500/30"
                      >
                        <RotateCcw className="h-3 w-3" /> Reinstate
                      </button>
                    ) : (
                      <button
                        onClick={() => suspend(r)}
                        data-testid={`signup-suspend-${r.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-amber-500/20 border border-amber-500/40 text-amber-100 hover:bg-amber-500/30"
                      >
                        <Ban className="h-3 w-3" /> Suspend
                      </button>
                    )}
                    <button
                      onClick={() => del(r)}
                      data-testid={`signup-delete-${r.id}`}
                      className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-red-500/20 border border-red-500/40 text-red-100 hover:bg-red-500/30"
                      title="Delete permanently"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="px-4 py-2 border-t border-white/5 text-[10px] uppercase tracking-widest text-zinc-500 flex justify-between">
            <span>Showing {shown.toLocaleString()} of {(totals.total || 0).toLocaleString()}</span>
            <span>Newest first</span>
          </div>
        </div>
      </div>
    </BrandShell>
  );
}
